import React, { useRef, useState } from 'react';
import { Button, Modal, Tooltip, message } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { deletePricingTemplate } from '../../services/Apis';

interface Props {
    iconOnly?: boolean;
    templateId: string;
    templateName: string;
    canDelete: boolean;
    blockReason?: string;
    onDeleted: () => void;
    onBlocked: (reason: string) => void;
    onTokenExpired: () => void;
}

const DeletePricingTemplateButton: React.FC<Props> = ({
    templateId, templateName, canDelete, blockReason, onDeleted, onBlocked, onTokenExpired, iconOnly = false,
}) => {
    const [open, setOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const inFlight = useRef(false);

    const handleDelete = async () => {
        if (inFlight.current || !canDelete) return;
        inFlight.current = true;
        setDeleting(true);
        try {
            await deletePricingTemplate(templateId);
            setOpen(false);
            message.success('Pricing template deleted.');
            onDeleted();
        } catch (error: any) {
            const status = error?.response?.status;
            const detail = error?.response?.data?.detail;
            const reason = typeof detail === 'string' ? detail : 'Unable to delete the template. Please try again.';
            if (status === 401) {
                setOpen(false);
                onTokenExpired();
            } else if (status === 404) {
                setOpen(false);
                message.info('This template is no longer available.');
                onDeleted();
            } else {
                message.error(reason);
                if (status === 409 || status === 403) {
                    setOpen(false);
                    onBlocked(reason);
                }
            }
        } finally {
            inFlight.current = false;
            setDeleting(false);
        }
    };

    return <>
        <Tooltip title={canDelete ? 'Delete template' : blockReason || 'Deletion is not available for this template.'}>
            <span>
                <Button danger icon={<DeleteOutlined />} disabled={!canDelete || deleting}
                    type={iconOnly ? 'text' : 'default'} size={iconOnly ? 'small' : 'middle'}
                    aria-label={`Delete template ${templateName}`} onClick={() => setOpen(true)}>
                    {iconOnly ? null : 'Delete'}
                </Button>
            </span>
        </Tooltip>
        <Modal title="Delete pricing template?" open={open} okText="Delete template" cancelText="Cancel"
            okButtonProps={{ danger: true, 'aria-label': 'Delete template' }} confirmLoading={deleting}
            cancelButtonProps={{ disabled: deleting }} closable={!deleting}
            maskClosable={!deleting} keyboard={!deleting}
            onCancel={() => { if (!inFlight.current) setOpen(false); }} onOk={handleDelete}>
            <p>Delete “{templateName}”? It will be removed from your templates and will no longer be available for use.</p>
            <p>Confirm this template is no longer needed. Older templates may have past deal usage that was not recorded. Templates with a recorded deal link cannot be deleted.</p>
        </Modal>
    </>;
};

export default DeletePricingTemplateButton;
